import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import { OrderStatusBadge } from "../../shared/OrderStatusBadge";
import { SettingRow } from "../../shared/SettingRow";
import Footer from "../components/Footer";
import { useAppContext } from "../../../context";
import { apiUrl } from "../../../utils/config";
import {
  X, Send, Bell, Ticket, CheckCheck,
  User, ShoppingCart, CreditCard, CalendarDays,
  Heart, MapPin, Headset, Settings, LogOut, ChevronRight,
  ClipboardList, RefreshCcw, FileText, Package, Star,
} from "lucide-react";
import { usePushSubscription } from "../../../hooks/usePushSubscription";

const API_BASE = apiUrl;

const formatCurrency = (value) =>
  new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
    maximumFractionDigits: 2,
  }).format(Number(value || 0));

const formatDate = (value) => {
  if (!value) return "Recent";

  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) {
    return value;
  }

  return parsed.toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
  });
};

const getInitials = (name) =>
  (name || "")
    .split(" ")
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part[0]?.toUpperCase() || "")
    .join("") || "GU";

const getTier = (ordersCount) => {
  if (ordersCount >= 10) return "Platinum";
  if (ordersCount >= 5) return "Gold";
  if (ordersCount >= 2) return "Silver";
  return "Starter";
};

const splitName = (name) => {
  const parts = (name || "").trim().split(/\s+/).filter(Boolean);
  return {
    firstName: parts[0] || "",
    lastName: parts.slice(1).join(" "),
  };
};

const normalizeOrderProduct = (item) => {
  if (item?.item) {
    return {
      ...item.item,
      id: item.item.id || item.menu_item_id,
      price: item.price ?? item.item.price ?? 0,
    };
  }

  return {
    id: item?.menu_item_id || item?.id,
    title: item?.name || "Item",
    price: item?.price ?? 0,
    images: item?.images,
  };
};

export const Profile = ({ subpage = "overview" }) => {
  const navigate = useNavigate();
  const {
    customerUser,
    logoutCustomer,
    wishlist,
    toggleWishlist,
    addToCart,
    cartItemsCount,
    updateUserProfile,
    storeSettings,
  } = useAppContext();

  const {
    supported: pushSupported,
    permission: pushPermission,
    subscribed: pushSubscribed,
    loading: pushLoading,
    subscribe: pushSubscribe,
    unsubscribe: pushUnsubscribe,
  } = usePushSubscription(customerUser?.id || null);

  const [activeTab, setActiveTab] = useState(subpage);
  const [editMode, setEditMode] = useState(false);
  const [editForm, setEditForm] = useState({
    name: "",
    phone: "",
    email: "",
  });
  const [orders, setOrders] = useState([]);
  const [ordersPage, setOrdersPage] = useState(1);
  const ORDERS_PER_PAGE = 5;
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [ratingOrder, setRatingOrder] = useState(null);   // order being rated
  const [ratingForm, setRatingForm] = useState({ rating: 5, comment: "" });
  const [submittingRating, setSubmittingRating] = useState(false);
  const [saving, setSaving] = useState(false);
  const [feedback, setFeedback] = useState({ type: "", text: "" });

  // Notifications
  const [notifs,         setNotifs]         = useState([]);
  const [notifsLoaded,   setNotifsLoaded]   = useState(false);
  const [unreadNotifs,   setUnreadNotifs]   = useState(0);

  // Support tickets
  const [tickets,        setTickets]        = useState([]);
  const [ticketsLoaded,  setTicketsLoaded]  = useState(false);
  const [activeTicket,   setActiveTicket]   = useState(null);
  const [loadingTicket,  setLoadingTicket]  = useState(false);
  const [replyText,      setReplyText]      = useState("");
  const [sendingReply,   setSendingReply]   = useState(false);
  const [newTicketOpen,  setNewTicketOpen]  = useState(false);
  const [newTicketForm,  setNewTicketForm]  = useState({ subject:"", category:"general", priority:"medium", description:"", order_id:"" });
  const [submittingNew,  setSubmittingNew]  = useState(false);
  const [newTicketError, setNewTicketError] = useState("");

  // Loyalty
  const [loyalty,        setLoyalty]        = useState(null);
  const [loyaltyLoaded,  setLoyaltyLoaded]  = useState(false);

  // Reservations
  const [myReservations,    setMyReservations]    = useState([]);
  const [reservationsLoaded, setReservationsLoaded] = useState(false);

  useEffect(() => {
    setActiveTab(subpage);
  }, [subpage]);

  useEffect(() => {
    if (!customerUser) {
      navigate("/login", { replace: true });
      return;
    }

    setEditForm({
      name:
        customerUser.name ||
        `${customerUser.firstName || ""} ${customerUser.lastName || ""}`.trim(),
      phone: customerUser.phone || "",
      email: customerUser.email || "",
    });
  }, [customerUser, navigate]);

  useEffect(() => {
    if (!customerUser) return;

    const loadOrders = async () => {
      try {
        const response = await axios.get(`${API_BASE}/bill`);
        const result = (response.data?.list || [])
          .map((order) => ({
            ...order,
            date: formatDate(order.created_at || order.updated_at || order.date),
            total: (order.items || []).reduce(
              (sum, item) => sum + Number(item.price || 0) * Number(item.quantity || 0),
              0
            ),
          }))
          .sort((first, second) => Number(second.id || 0) - Number(first.id || 0));

        setOrders(result);
      } catch (error) {
        setOrders([]);
      }
    };

    const loadLoyalty = async () => {
      try {
        const res = await axios.get(`${API_BASE}/loyalty?user_id=${customerUser.id}`);
        if (res.data.status) setLoyalty(res.data);
      } catch (_) {}
      finally { setLoyaltyLoaded(true); }
    };

    loadOrders();
    loadLoyalty();
  }, [customerUser]);

  useEffect(() => {
    if (activeTab === "support" && !ticketsLoaded && customerUser?.id) {
      axios.get(`${API_BASE}/tickets/customer`, { params: { customer_id: customerUser.id } })
        .then(res => { if (res.data.status) setTickets(res.data.tickets || []); })
        .catch(() => {})
        .finally(() => setTicketsLoaded(true));
    }
    if (activeTab === "notifications" && !notifsLoaded && customerUser?.id) {
      axios.get(`${API_BASE}/notifications/customer/${customerUser.id}`)
        .then(res => {
          if (res.data.status) {
            setNotifs(res.data.notifications || []);
            setUnreadNotifs(res.data.unread || 0);
          }
        })
        .catch(() => {})
        .finally(() => setNotifsLoaded(true));
    }
    if (activeTab === "reservations" && !reservationsLoaded && customerUser?.id) {
      axios.get(`${API_BASE}/reservations/user/${customerUser.id}`)
        .then(res => { if (res.data.status) setMyReservations(res.data.list || []); })
        .catch(() => {})
        .finally(() => setReservationsLoaded(true));
    }
  }, [activeTab, ticketsLoaded, notifsLoaded, loyaltyLoaded, reservationsLoaded, customerUser]);

  const openTicketThread = async (t) => {
    setLoadingTicket(true);
    setActiveTicket(null);
    setReplyText("");
    try {
      const res = await axios.get(`${API_BASE}/tickets/${t.id}`);
      if (res.data.status) setActiveTicket(res.data.ticket);
    } catch (_) {}
    setLoadingTicket(false);
  };

  const sendTicketReply = async () => {
    if (!replyText.trim() || !activeTicket) return;
    setSendingReply(true);
    try {
      await axios.post(`${API_BASE}/tickets/${activeTicket.id}/messages`, {
        sender_type: "customer",
        sender_name: activeTicket.customer_name,
        message: replyText.trim(),
      });
      const res = await axios.get(`${API_BASE}/tickets/${activeTicket.id}`);
      if (res.data.status) setActiveTicket(res.data.ticket);
      setReplyText("");
    } catch (_) {}
    setSendingReply(false);
  };

  if (!customerUser) {
    return null;
  }

  const userName =
    customerUser.name ||
    `${customerUser.firstName || ""} ${customerUser.lastName || ""}`.trim() ||
    "Gamapa User";
  const userSince = customerUser.joined || "New member";
  const userPoints = loyalty?.balance ?? orders.reduce((sum, order) => sum + Math.round(Number(order.total || 0)), 0);
  const userTier = getTier(orders.length);
  const primaryAddress = customerUser.address || "Add your delivery address";
  const addresses = customerUser.addresses?.length
    ? customerUser.addresses
    : [
        {
          id: "primary",
          type: "Home",
          fullName: userName,
          line1: primaryAddress,
        },
      ];

  const h = (event) => {
    const { name, value } = event.target;
    setEditForm((current) => ({ ...current, [name]: value }));
    setFeedback({ type: "", text: "" });
  };

  const handleSaveProfile = async () => {
    setSaving(true);
    setFeedback({ type: "", text: "" });

    const { firstName, lastName } = splitName(editForm.name);
    const updatedUser = await updateUserProfile({
      firstName,
      lastName,
      email: editForm.email,
      phone: editForm.phone,
      name: editForm.name,
    });

    if (!updatedUser) {
      setFeedback({ type: "error", text: "Unable to update your profile right now." });
      setSaving(false);
      return;
    }

    setEditMode(false);
    setSaving(false);
    setFeedback({ type: "success", text: "Profile updated successfully." });
  };

  const handleLogout = () => {
    logoutCustomer();
    navigate("/login", { replace: true });
  };

  const handleReorder = (order) => {
    (order.items || []).forEach((item) => {
      addToCart(normalizeOrderProduct(item));
    });

    setFeedback({ type: "success", text: "Order items added to cart." });
  };

  const handleDownloadInvoice = (order) => {
    const storeName = storeSettings?.store_name || "Gamapa";
    const storeEmail = storeSettings?.store_email || "";
    const storePhone = storeSettings?.store_phone || "";
    const storeAddress = storeSettings?.store_address || "";
    const currency = storeSettings?.store_currency || "USD";
    const taxRate = Number(storeSettings?.tax_rate || 0);

    const fmt = (v) =>
      new Intl.NumberFormat("en-US", {
        style: "currency",
        currency,
        maximumFractionDigits: 2,
      }).format(Number(v || 0));

    const subtotal = (order.items || []).reduce(
      (sum, item) => sum + Number(item.price || 0) * Number(item.quantity || 1),
      0
    );
    const taxAmount = subtotal * (taxRate / 100);
    const total = subtotal + taxAmount;

    const doc = new jsPDF({ orientation: "portrait", unit: "mm", format: "a4" });
    const pageW = doc.internal.pageSize.getWidth();

    // ── Header band ──
    doc.setFillColor(249, 115, 22); // orange-500
    doc.rect(0, 0, pageW, 36, "F");
    doc.setTextColor(255, 255, 255);
    doc.setFontSize(22);
    doc.setFont("helvetica", "bold");
    doc.text(storeName, 14, 16);
    doc.setFontSize(10);
    doc.setFont("helvetica", "normal");
    doc.text("INVOICE", pageW - 14, 16, { align: "right" });
    doc.setFontSize(9);
    const headerLines = [storeEmail, storePhone, storeAddress].filter(Boolean);
    headerLines.forEach((line, i) => doc.text(line, 14, 24 + i * 5));
    doc.text(`#${order.id}`, pageW - 14, 24, { align: "right" });
    doc.text(`Date: ${order.date || "—"}`, pageW - 14, 29, { align: "right" });
    doc.text(`Status: ${order.status || "—"}`, pageW - 14, 34, { align: "right" });

    // ── Bill-to section ──
    doc.setTextColor(55, 65, 81);
    doc.setFontSize(9);
    doc.setFont("helvetica", "bold");
    doc.text("BILLED TO", 14, 48);
    doc.setFont("helvetica", "normal");
    const billedName =
      customerUser.name ||
      `${customerUser.firstName || ""} ${customerUser.lastName || ""}`.trim() ||
      "Customer";
    doc.text(billedName, 14, 54);
    if (customerUser.email) doc.text(customerUser.email, 14, 59);
    if (customerUser.phone) doc.text(customerUser.phone, 14, 64);

    // ── Items table ──
    const rows = (order.items || []).map((item) => {
      const name = item?.item?.name || item?.name || "Item";
      const qty = Number(item.quantity || 1);
      const price = Number(item.price || 0);
      return [name, qty, fmt(price), fmt(price * qty)];
    });

    autoTable(doc, {
      startY: 72,
      head: [["Item", "Qty", "Unit Price", "Total"]],
      body: rows,
      theme: "striped",
      headStyles: {
        fillColor: [249, 115, 22],
        textColor: 255,
        fontStyle: "bold",
        fontSize: 9,
      },
      bodyStyles: { fontSize: 9, textColor: [55, 65, 81] },
      columnStyles: {
        0: { cellWidth: "auto" },
        1: { halign: "center", cellWidth: 20 },
        2: { halign: "right", cellWidth: 35 },
        3: { halign: "right", cellWidth: 35 },
      },
      margin: { left: 14, right: 14 },
    });

    const finalY = doc.lastAutoTable.finalY + 8;

    // ── Totals block ──
    const col2X = pageW - 14;
    const col1X = pageW - 65;

    doc.setFontSize(9);
    doc.setTextColor(100, 116, 139);
    doc.text("Subtotal", col1X, finalY);
    doc.text(fmt(subtotal), col2X, finalY, { align: "right" });

    if (taxRate > 0) {
      doc.text(`Tax (${taxRate}%)`, col1X, finalY + 6);
      doc.text(fmt(taxAmount), col2X, finalY + 6, { align: "right" });
    }

    doc.setDrawColor(249, 115, 22);
    doc.setLineWidth(0.4);
    const lineY = finalY + (taxRate > 0 ? 10 : 4);
    doc.line(col1X, lineY, col2X, lineY);

    doc.setFontSize(11);
    doc.setFont("helvetica", "bold");
    doc.setTextColor(249, 115, 22);
    doc.text("Total", col1X, lineY + 7);
    doc.text(fmt(total), col2X, lineY + 7, { align: "right" });

    // ── Footer ──
    doc.setFontSize(8);
    doc.setFont("helvetica", "normal");
    doc.setTextColor(156, 163, 175);
    doc.text(
      `Thank you for ordering from ${storeName}!`,
      pageW / 2,
      doc.internal.pageSize.getHeight() - 10,
      { align: "center" }
    );

    doc.save(`invoice-order-${order.id}.pdf`);
  };

  const tabs = [
    { id: "overview",      icon: User,         label: "Profile" },
    { id: "orders",        icon: ShoppingCart,  label: "Orders" },
    { id: "loyalty",       icon: CreditCard,    label: "Loyalty Card" },
    { id: "reservations",  icon: CalendarDays,  label: "My Reservations" },
    { id: "wishlist",      icon: Heart,         label: "Wishlist" },
    { id: "address",       icon: MapPin,        label: "Address" },
    { id: "support",       icon: Headset,       label: "Support" },
    { id: "notifications", icon: Bell,          label: "Notifications", badge: unreadNotifs },
    { id: "settings",      icon: Settings,      label: "Settings" },
  ];

  const inputCls = "w-full px-4 py-3 border border-gray-200 rounded-xl text-sm bg-white";

  return (
    <>
      <div className="max-w-6xl mx-auto px-4 py-10">
        <div className="grid md:grid-cols-4 gap-6">
          <div className="md:col-span-1">
            <div className="bg-white rounded-3xl p-5 border border-gray-100 shadow-sm mb-4">
              <div className="text-center mb-4">
                <div className="w-20 h-20 bg-gradient-to-br from-[#f97316] to-red-500 rounded-3xl flex items-center justify-center text-white font-black text-2xl mx-auto mb-3 shadow-lg shadow-orange-200">
                  {getInitials(userName)}
                </div>
                <p className="font-bold text-gray-900">{userName}</p>
                <p className="text-xs text-gray-400 mb-2">{customerUser.email}</p>
                <span className="bg-amber-100 text-amber-700 text-xs font-black px-3 py-1 rounded-full">
                  ⭐ {userTier} Member
                </span>
              </div>
              <div className="bg-orange-50 rounded-2xl p-3 text-center border border-orange-100">
                <div className="font-black text-2xl text-[#f97316]">{userPoints}</div>
                <div className="text-xs text-gray-500">GamapaPoints</div>
                <div className="text-[10px] text-gray-400 mt-0.5">
                  ≈ {formatCurrency(userPoints / 20)} value
                </div>
              </div>
            </div>

            <nav className="bg-white rounded-3xl border border-gray-100 shadow-sm overflow-hidden">
              <div className="p-2 space-y-0.5">
                {tabs.map((tab) => {
                  const Icon = tab.icon;
                  const isActive = activeTab === tab.id;
                  return (
                    <button
                      key={tab.id}
                      onClick={() => setActiveTab(tab.id)}
                      className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm font-medium transition-all duration-150 group ${
                        isActive
                          ? "bg-orange-50 text-[#f97316]"
                          : "text-gray-500 hover:bg-gray-50 hover:text-gray-800"
                      }`}
                      type="button"
                    >
                      <span className={`flex-shrink-0 w-8 h-8 flex items-center justify-center rounded-lg transition-all duration-150 ${
                        isActive
                          ? "bg-[#f97316] text-white shadow-sm shadow-orange-200"
                          : "bg-gray-100 text-gray-400 group-hover:bg-gray-200 group-hover:text-gray-600"
                      }`}>
                        <Icon size={15} strokeWidth={2} />
                      </span>
                      <span className="flex-1 text-left">{tab.label}</span>
                      {tab.badge > 0 && (
                        <span className="bg-orange-500 text-white text-[10px] font-black w-5 h-5 flex items-center justify-center rounded-full leading-none flex-shrink-0">
                          {tab.badge}
                        </span>
                      )}
                      {isActive && <ChevronRight size={14} className="text-orange-300 flex-shrink-0" />}
                    </button>
                  );
                })}
              </div>

              <div className="px-2 pb-2">
                <div className="border-t border-gray-100 pt-2">
                  <button
                    onClick={handleLogout}
                    className="w-full flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm font-medium text-gray-400 hover:bg-red-50 hover:text-red-500 transition-all duration-150 group"
                    type="button"
                  >
                    <span className="flex-shrink-0 w-8 h-8 flex items-center justify-center rounded-lg bg-gray-100 text-gray-400 group-hover:bg-red-100 group-hover:text-red-400 transition-all duration-150">
                      <LogOut size={15} strokeWidth={2} />
                    </span>
                    Sign Out
                  </button>
                </div>
              </div>
            </nav>
          </div>

          <div className="md:col-span-3">
            {feedback.text ? (
              <div
                className={`mb-4 rounded-2xl border px-4 py-3 text-sm font-medium ${
                  feedback.type === "error"
                    ? "border-red-100 bg-red-50 text-red-500"
                    : "border-green-100 bg-green-50 text-green-600"
                }`}
              >
                {feedback.text}
              </div>
            ) : null}

            {activeTab === "overview" ? (
              <div className="space-y-4 afu">
                <div className="bg-white rounded-3xl p-6 border border-gray-100 shadow-sm">
                  <div className="flex items-center justify-between mb-6">
                    <h2 className="font-serif text-2xl text-gray-900">Personal Information</h2>
                    <button
                      onClick={() => setEditMode((current) => !current)}
                      className={`text-sm font-bold px-4 py-2 rounded-xl btn-press transition-colors ${
                        editMode
                          ? "bg-gray-100 text-gray-600"
                          : "bg-orange-50 text-[#f97316] hover:bg-orange-100"
                      }`}
                      type="button"
                    >
                      {editMode ? "Cancel" : "✏️ Edit"}
                    </button>
                  </div>

                  {editMode ? (
                    <div className="space-y-3">
                      <div className="grid grid-cols-2 gap-3">
                        <div>
                          <label className="text-xs font-bold text-gray-500 mb-1 block">
                            Full Name
                          </label>
                          <input
                            name="name"
                            value={editForm.name}
                            onChange={h}
                            className={inputCls}
                          />
                        </div>
                        <div>
                          <label className="text-xs font-bold text-gray-500 mb-1 block">
                            Phone
                          </label>
                          <input
                            name="phone"
                            value={editForm.phone}
                            onChange={h}
                            className={inputCls}
                          />
                        </div>
                      </div>
                      <div>
                        <label className="text-xs font-bold text-gray-500 mb-1 block">
                          Email
                        </label>
                        <input
                          name="email"
                          value={editForm.email}
                          onChange={h}
                          className={inputCls}
                        />
                      </div>
                      <button
                        onClick={handleSaveProfile}
                        className="bg-[#f97316] text-white font-bold px-6 py-2.5 rounded-xl btn-press hover:bg-orange-600 mt-2 disabled:opacity-60"
                        disabled={saving}
                        type="button"
                      >
                        {saving ? "Saving..." : "Save Changes"}
                      </button>
                    </div>
                  ) : (
                    <div className="grid grid-cols-2 gap-4">
                      {[
                        ["👤 Name", userName],
                        ["📧 Email", customerUser.email || "No email available"],
                        ["📱 Phone", customerUser.phone || "No phone added"],
                        ["📅 Member Since", userSince],
                      ].map(([label, value]) => (
                        <div key={label} className="bg-gray-50 rounded-xl p-4">
                          <div className="text-xs text-gray-400 mb-1">{label}</div>
                          <div className="font-semibold text-gray-900 text-sm">{value}</div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>

                <div className="grid grid-cols-3 gap-4">
                  {[
                    ["🛒", "Total Orders", orders.length],
                    ["❤️", "Wishlist Items", wishlist.length],
                    ["📦", "Cart Items", cartItemsCount],
                  ].map(([icon, label, value]) => (
                    <div
                      key={label}
                      className="bg-white rounded-2xl p-5 border border-gray-100 shadow-sm text-center card-hover"
                    >
                      <div className="text-3xl mb-2">{icon}</div>
                      <div className="font-black text-2xl text-gray-900 mb-0.5">{value}</div>
                      <div className="text-xs text-gray-400">{label}</div>
                    </div>
                  ))}
                </div>

                <div className="bg-white rounded-3xl p-6 border border-gray-100 shadow-sm">
                  <div className="flex items-center justify-between mb-4">
                    <h3 className="font-bold text-gray-900">Recent Orders</h3>
                    <button
                      onClick={() => setActiveTab("orders")}
                      className="text-sm font-bold text-[#f97316] hover:underline"
                      type="button"
                    >
                      View All →
                    </button>
                  </div>
                  <div className="space-y-3">
                    {orders.slice(0, 2).map((order) => (
                      <div key={order.id} className="flex items-center gap-4 bg-gray-50 rounded-xl p-3">
                        <img
                          src={order?.items?.[0]?.item?.images?.image}
                          alt=""
                          className="w-12 h-12 rounded-xl object-cover flex-shrink-0"
                        />
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 mb-0.5">
                            <span className="font-bold text-sm text-gray-900">#{order.id}</span>
                            <OrderStatusBadge status={order.status} />
                          </div>
                          <p className="text-xs text-gray-400">
                            {order.date} · {order.items?.length || 0} items · {formatCurrency(order.total)}
                          </p>
                        </div>
                        <div className="flex gap-2 flex-shrink-0">
                          <button
                            onClick={() => setSelectedOrder(order)}
                            className="text-xs font-bold text-gray-500 bg-gray-100 px-3 py-1.5 rounded-lg hover:bg-gray-200 btn-press"
                            type="button"
                          >
                            View
                          </button>
                          <button
                            onClick={() => handleReorder(order)}
                            className="text-xs font-bold text-[#f97316] bg-orange-50 px-3 py-1.5 rounded-lg hover:bg-orange-100 btn-press"
                            type="button"
                          >
                            Reorder
                          </button>
                        </div>
                      </div>
                    ))}
                    {!orders.length ? (
                      <div className="bg-gray-50 rounded-xl p-6 text-sm text-gray-500 text-center">
                        No orders found yet.
                      </div>
                    ) : null}
                  </div>
                </div>
              </div>
            ) : null}

            {activeTab === "orders" ? (() => {
              const totalPages = Math.ceil(orders.length / ORDERS_PER_PAGE);
              const pagedOrders = orders.slice((ordersPage - 1) * ORDERS_PER_PAGE, ordersPage * ORDERS_PER_PAGE);
              return (
              <div className="afu">
                {/* Header */}
                <div className="flex items-center justify-between mb-6">
                  <div>
                    <h2 className="font-serif text-2xl text-gray-900">Order History</h2>
                    {orders.length > 0 && (
                      <p className="text-xs text-gray-400 mt-0.5">{orders.length} order{orders.length !== 1 ? "s" : ""} placed</p>
                    )}
                  </div>
                </div>

                {/* Empty state */}
                {!orders.length ? (
                  <div className="bg-white rounded-3xl border border-gray-100 shadow-sm p-12 text-center">
                    <div className="w-16 h-16 bg-orange-50 rounded-2xl flex items-center justify-center mx-auto mb-4">
                      <span className="text-3xl">🛍️</span>
                    </div>
                    <p className="font-semibold text-gray-700 mb-1">No orders yet</p>
                    <p className="text-sm text-gray-400">Your order history will appear here once you place your first order.</p>
                  </div>
                ) : (
                  <>
                  <div className="space-y-3">
                    {pagedOrders.map((order) => {
                      const isDelivered = ["delivered", "Delivered"].includes(order.status);
                      const isCancelled = ["cancelled", "Cancelled"].includes(order.status);
                      const isActive = !isDelivered && !isCancelled;
                      const extraItems = (order.items?.length || 0) - 3;

                      return (
                        <div
                          key={order.id}
                          className="bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden"
                        >
                          {/* Accent bar */}
                          <div
                            className="h-[3px] w-full"
                            style={{
                              background: isDelivered
                                ? "linear-gradient(90deg,#22c55e,#16a34a)"
                                : isCancelled
                                ? "linear-gradient(90deg,#cbd5e1,#94a3b8)"
                                : "linear-gradient(90deg,#f97316,#fb923c)",
                            }}
                          />

                          {/* Card body */}
                          <div className="p-4">
                            {/* Top row */}
                            <div className="flex items-start justify-between gap-3 mb-3">
                              <div className="flex items-center gap-3 min-w-0">
                                {/* Item thumbnails */}
                                <div className="flex -space-x-2.5 shrink-0">
                                  {(order.items || []).slice(0, 3).map((item, i) => {
                                    const img = item?.item?.images?.image || item?.images?.image;
                                    return img ? (
                                      <img
                                        key={i}
                                        src={img}
                                        alt=""
                                        className="w-11 h-11 rounded-xl object-cover border-2 border-white shadow-sm"
                                        style={{ zIndex: 3 - i }}
                                      />
                                    ) : (
                                      <div
                                        key={i}
                                        className="w-11 h-11 rounded-xl border-2 border-white bg-orange-50 flex items-center justify-center text-lg shadow-sm"
                                        style={{ zIndex: 3 - i }}
                                      >
                                        🍽️
                                      </div>
                                    );
                                  })}
                                  {extraItems > 0 && (
                                    <div className="w-11 h-11 rounded-xl border-2 border-white bg-gray-100 flex items-center justify-center text-xs font-bold text-gray-500 shadow-sm" style={{ zIndex: 0 }}>
                                      +{extraItems}
                                    </div>
                                  )}
                                </div>

                                {/* Order meta */}
                                <div className="min-w-0">
                                  <div className="flex items-center gap-2 flex-wrap">
                                    <span className="font-black text-gray-900 text-sm tracking-tight">#{order.id}</span>
                                    <OrderStatusBadge status={order.status} />
                                  </div>
                                  <p className="text-xs text-gray-400 mt-0.5">{order.date}</p>
                                </div>
                              </div>

                              {/* Total */}
                              <div className="text-right shrink-0">
                                <div className="font-black text-[#f97316] text-xl leading-none">{formatCurrency(order.total)}</div>
                                <div className="text-xs text-gray-400 mt-1">{order.items?.length || 0} item{order.items?.length !== 1 ? "s" : ""}</div>
                              </div>
                            </div>

                            {/* Item chips */}
                            <div className="flex flex-wrap gap-1.5 mb-4">
                              {(order.items || []).slice(0, 4).map((item, index) => (
                                <span
                                  key={`${order.id}-${index}`}
                                  className="inline-flex items-center gap-1 text-xs bg-gray-50 text-gray-600 border border-gray-100 px-2.5 py-1 rounded-full"
                                >
                                  {item?.item?.name || item?.name || "Item"}
                                  {item.quantity > 1 && <span className="text-[10px] bg-gray-200 text-gray-500 rounded-full px-1.5 py-0.5 font-semibold">×{item.quantity}</span>}
                                </span>
                              ))}
                              {(order.items?.length || 0) > 4 && (
                                <span className="text-xs text-gray-400 px-2 py-1">+{order.items.length - 4} more</span>
                              )}
                            </div>

                            {/* Actions */}
                            <div className="flex items-center gap-2 flex-wrap">
                              <button
                                onClick={() => setSelectedOrder(order)}
                                className="inline-flex items-center gap-1.5 text-xs font-semibold text-gray-600 bg-gray-50 border border-gray-200 px-3 py-2 rounded-lg hover:bg-gray-100 active:scale-[.97] transition-all"
                                type="button"
                              >
                                <ClipboardList size={13} strokeWidth={2} /> Details
                              </button>
                              <button
                                onClick={() => handleReorder(order)}
                                className="inline-flex items-center gap-1.5 text-xs font-semibold text-white px-3 py-2 rounded-lg active:scale-[.97] transition-all"
                                style={{ background: "linear-gradient(135deg,#f97316,#ea580c)" }}
                                type="button"
                              >
                                <RefreshCcw size={13} strokeWidth={2} /> Reorder
                              </button>

                              <div className="flex items-center gap-1.5 ml-auto">
                                <button
                                  onClick={() => handleDownloadInvoice(order)}
                                  title="Download invoice"
                                  className="w-8 h-8 flex items-center justify-center text-gray-400 bg-gray-50 border border-gray-200 rounded-lg hover:bg-gray-100 hover:text-gray-600 active:scale-[.97] transition-all"
                                  type="button"
                                >
                                  <FileText size={13} strokeWidth={2} />
                                </button>
                                {isActive && (
                                  <button
                                    onClick={() => navigate(`/track/${order.id}`)}
                                    title="Track order"
                                    className="w-8 h-8 flex items-center justify-center text-orange-500 bg-orange-50 border border-orange-100 rounded-lg hover:bg-orange-100 active:scale-[.97] transition-all"
                                    type="button"
                                  >
                                    <Package size={13} strokeWidth={2} />
                                  </button>
                                )}
                                {isDelivered && (
                                  <button
                                    onClick={() => { setRatingOrder(order); setRatingForm({ rating: 5, comment: "" }); }}
                                    title="Rate order"
                                    className="w-8 h-8 flex items-center justify-center text-amber-500 bg-amber-50 border border-amber-100 rounded-lg hover:bg-amber-100 active:scale-[.97] transition-all"
                                    type="button"
                                  >
                                    <Star size={13} strokeWidth={2} />
                                  </button>
                                )}
                              </div>
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>

                  {/* Pagination */}
                  {totalPages > 1 && (
                    <div className="flex items-center justify-between mt-5 px-1">
                      <p className="text-xs text-gray-400">
                        Page {ordersPage} of {totalPages}
                      </p>
                      <div className="flex items-center gap-1.5">
                        <button
                          onClick={() => setOrdersPage((p) => Math.max(1, p - 1))}
                          disabled={ordersPage === 1}
                          className="w-9 h-9 flex items-center justify-center rounded-xl border border-gray-200 bg-white text-gray-600 text-sm font-bold hover:bg-gray-50 active:scale-95 transition-all disabled:opacity-30 disabled:cursor-not-allowed"
                          type="button"
                        >
                          ‹
                        </button>
                        {Array.from({ length: totalPages }, (_, i) => i + 1).map((pg) => (
                          <button
                            key={pg}
                            onClick={() => setOrdersPage(pg)}
                            className="w-9 h-9 flex items-center justify-center rounded-xl text-xs font-bold transition-all active:scale-95"
                            style={
                              pg === ordersPage
                                ? { background: "linear-gradient(135deg,#f97316,#ea580c)", color: "#fff", border: "none" }
                                : { background: "#fff", color: "#374151", border: "1px solid #e5e7eb" }
                            }
                            type="button"
                          >
                            {pg}
                          </button>
                        ))}
                        <button
                          onClick={() => setOrdersPage((p) => Math.min(totalPages, p + 1))}
                          disabled={ordersPage === totalPages}
                          className="w-9 h-9 flex items-center justify-center rounded-xl border border-gray-200 bg-white text-gray-600 text-sm font-bold hover:bg-gray-50 active:scale-95 transition-all disabled:opacity-30 disabled:cursor-not-allowed"
                          type="button"
                        >
                          ›
                        </button>
                      </div>
                    </div>
                  )}
                  </>
                )}
              </div>
              );
            })() : null}

            {activeTab === "loyalty" ? (
              <div className="afu space-y-4">
                <h2 className="font-serif text-2xl text-gray-900">Loyalty Card</h2>

                {!loyaltyLoaded ? (
                  <div className="bg-white rounded-3xl p-8 border border-gray-100 shadow-sm text-center text-gray-400 text-sm">
                    Loading your loyalty card…
                  </div>
                ) : (
                  <>
                    {/* Card visual */}
                    {loyalty?.card && (
                      <div
                        className={`relative rounded-2xl p-5 text-white overflow-hidden ${
                          loyalty.card.status === "active"
                            ? "bg-gradient-to-br from-orange-500 via-orange-400 to-red-500 shadow-lg shadow-orange-200"
                            : "bg-gradient-to-br from-gray-400 to-gray-500 shadow-md"
                        }`}
                      >
                        <div className="absolute -top-6 -right-6 w-28 h-28 bg-white/10 rounded-full" />
                        <div className="absolute -bottom-8 -left-4 w-36 h-36 bg-white/10 rounded-full" />
                        <div className="relative z-10">
                          <div className="flex justify-between items-start mb-6">
                            <div>
                              <p className="text-[10px] font-semibold opacity-70 uppercase tracking-widest">Gamapa Loyalty</p>
                              <p className="text-sm font-bold mt-0.5">Virtual Card</p>
                            </div>
                            <span className="text-[10px] font-black px-2 py-0.5 rounded-full bg-white/20">
                              {loyalty.card.status === "active" ? "ACTIVE" : "BLOCKED"}
                            </span>
                          </div>
                          <p className="font-mono text-lg tracking-widest font-bold mb-5">
                            {loyalty.card.card_number}
                          </p>
                          <div className="flex justify-between items-end">
                            <div>
                              <p className="text-[10px] opacity-60 uppercase">Card Holder</p>
                              <p className="text-sm font-bold">{customerUser.name || "—"}</p>
                            </div>
                            <div className="text-right">
                              <p className="text-[10px] opacity-60 uppercase">Member Since</p>
                              <p className="text-sm font-bold">
                                {loyalty.card.created_at
                                  ? new Date(loyalty.card.created_at).getFullYear()
                                  : new Date().getFullYear()}
                              </p>
                            </div>
                          </div>
                        </div>
                      </div>
                    )}

                    {/* Points balance */}
                    <div className="bg-white rounded-3xl p-5 border border-gray-100 shadow-sm">
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="text-xs text-gray-400 font-semibold uppercase tracking-wide mb-1">Points Balance</p>
                          <p className="text-4xl font-black text-[#f97316]">{(loyalty?.balance ?? 0).toLocaleString()}</p>
                          <p className="text-sm text-gray-400 mt-1">
                            ≈ {formatCurrency((loyalty?.balance ?? 0) / 20)} value &nbsp;·&nbsp; 20 pts = $1
                          </p>
                        </div>
                        <div className="w-16 h-16 bg-orange-100 rounded-2xl flex items-center justify-center text-3xl">
                          🎁
                        </div>
                      </div>
                    </div>

                    {/* Points history */}
                    <div className="bg-white rounded-3xl p-5 border border-gray-100 shadow-sm">
                      <h3 className="font-bold text-gray-800 mb-4">Points History</h3>
                      {!loyalty?.transactions?.length ? (
                        <p className="text-sm text-gray-400 text-center py-6">No transactions yet. Earn points by placing orders!</p>
                      ) : (
                        <div className="space-y-2">
                          {loyalty.transactions.map((tx) => {
                            const typeMap = {
                              earn:   { label: "Earned",   cls: "bg-emerald-100 text-emerald-700" },
                              redeem: { label: "Redeemed", cls: "bg-red-100 text-red-600" },
                              manual: { label: "Adjusted", cls: "bg-blue-100 text-blue-700" },
                            };
                            const { label, cls } = typeMap[tx.type] || typeMap.manual;
                            return (
                              <div key={tx.id} className="flex items-center justify-between py-2.5 border-b border-gray-50 last:border-0">
                                <div className="flex items-center gap-2">
                                  <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full ${cls}`}>{label}</span>
                                  <span className="text-sm text-gray-600">{tx.description || "—"}</span>
                                </div>
                                <div className="text-right">
                                  <span className={`text-sm font-black ${tx.points > 0 ? "text-emerald-600" : "text-red-500"}`}>
                                    {tx.points > 0 ? "+" : ""}{tx.points} pts
                                  </span>
                                  <p className="text-[10px] text-gray-400">
                                    {tx.created_at ? new Date(tx.created_at).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" }) : ""}
                                  </p>
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      )}
                    </div>
                  </>
                )}
              </div>
            ) : null}

            {activeTab === "reservations" ? (
              <div className="afu space-y-4">
                <div className="flex items-center justify-between">
                  <h2 className="font-serif text-2xl text-gray-900">My Reservations</h2>
                  <button
                    onClick={() => navigate("/reservations")}
                    className="flex items-center gap-1.5 text-sm font-bold text-white bg-[#f97316] hover:bg-orange-600 px-4 py-2 rounded-xl transition"
                    type="button"
                  >
                    + New Booking
                  </button>
                </div>

                {!reservationsLoaded ? (
                  <div className="space-y-3">
                    {[1,2,3].map(i => (
                      <div key={i} className="bg-white rounded-2xl border border-gray-100 shadow-sm p-5 animate-pulse">
                        <div className="flex gap-4">
                          <div className="w-14 h-14 bg-gray-100 rounded-xl flex-shrink-0" />
                          <div className="flex-1 space-y-2">
                            <div className="h-3 bg-gray-100 rounded w-1/3" />
                            <div className="h-3 bg-gray-100 rounded w-1/2" />
                            <div className="h-3 bg-gray-100 rounded w-1/4" />
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : myReservations.length === 0 ? (
                  <div className="bg-white rounded-3xl p-12 border border-gray-100 shadow-sm text-center">
                    <div className="w-16 h-16 bg-orange-50 rounded-2xl flex items-center justify-center mx-auto mb-4">
                      <span className="text-3xl">🍽</span>
                    </div>
                    <p className="font-bold text-gray-800 mb-1">No reservations yet</p>
                    <p className="text-sm text-gray-400 mb-5">Book a table and it'll show up here.</p>
                    <button
                      onClick={() => navigate("/reservations")}
                      className="inline-flex items-center gap-2 bg-[#f97316] hover:bg-orange-600 text-white text-sm font-bold px-5 py-2.5 rounded-xl transition"
                      type="button"
                    >
                      Reserve a Table
                    </button>
                  </div>
                ) : (
                  <div className="space-y-3">
                    {myReservations.map((r) => {
                      const statusMap = {
                        pending:   { dot: "bg-amber-400",   pill: "bg-amber-50 text-amber-700 border-amber-100",     label: "Pending",   icon: "⏳" },
                        confirmed: { dot: "bg-blue-400",    pill: "bg-blue-50 text-blue-700 border-blue-100",         label: "Confirmed", icon: "✅" },
                        seated:    { dot: "bg-purple-400",  pill: "bg-purple-50 text-purple-700 border-purple-100",   label: "Seated",    icon: "🪑" },
                        completed: { dot: "bg-emerald-400", pill: "bg-emerald-50 text-emerald-700 border-emerald-100",label: "Completed", icon: "🎉" },
                        cancelled: { dot: "bg-red-300",     pill: "bg-red-50 text-red-500 border-red-100",            label: "Cancelled", icon: "✕"  },
                      };
                      const s = statusMap[r.status?.toLowerCase()] || statusMap.pending;
                      const isCancelled = r.status?.toLowerCase() === "cancelled";

                      return (
                        <div
                          key={r.id}
                          className={`bg-white rounded-2xl border shadow-sm overflow-hidden transition ${
                            isCancelled ? "border-gray-100 opacity-60" : "border-gray-100 hover:border-orange-100 hover:shadow-md"
                          }`}
                        >
                          <div className="flex items-stretch">
                            {/* Date column */}
                            <div className={`flex flex-col items-center justify-center px-5 py-4 flex-shrink-0 ${isCancelled ? "bg-gray-50" : "bg-orange-50"}`}>
                              <span className={`text-2xl font-extrabold leading-none ${isCancelled ? "text-gray-400" : "text-[#f97316]"}`}>
                                {r.date ? new Date(r.date).getDate() : "—"}
                              </span>
                              <span className={`text-[10px] font-bold uppercase tracking-wide mt-0.5 ${isCancelled ? "text-gray-400" : "text-orange-400"}`}>
                                {r.date ? new Date(r.date).toLocaleDateString("en-US", { month: "short" }) : ""}
                              </span>
                              <span className={`text-[10px] mt-0.5 ${isCancelled ? "text-gray-400" : "text-orange-300"}`}>
                                {r.date ? new Date(r.date).toLocaleDateString("en-US", { weekday: "short" }) : ""}
                              </span>
                            </div>

                            {/* Main info */}
                            <div className="flex-1 min-w-0 px-4 py-4">
                              <div className="flex items-center gap-2 mb-2">
                                {r.table_name && (
                                  <span className="text-xs font-bold text-gray-800 bg-gray-100 px-2.5 py-0.5 rounded-full">
                                    🪑 {r.table_name}
                                  </span>
                                )}
                                <span className={`text-xs font-bold px-2.5 py-0.5 rounded-full border ${s.pill}`}>
                                  {s.icon} {s.label}
                                </span>
                              </div>

                              <div className="flex flex-wrap gap-3 text-sm text-gray-600">
                                <span className="flex items-center gap-1">
                                  <span className="text-orange-400">🕐</span>
                                  {r.time || "—"}
                                </span>
                                <span className="flex items-center gap-1">
                                  <span className="text-orange-400">👥</span>
                                  {r.party_size} guest{r.party_size !== 1 ? "s" : ""}
                                </span>
                                {r.customer_name && (
                                  <span className="flex items-center gap-1 text-gray-400 text-xs">
                                    {r.customer_name}
                                  </span>
                                )}
                              </div>

                              {r.notes && (
                                <p className="text-xs text-gray-400 mt-2 italic line-clamp-1">
                                  "{r.notes}"
                                </p>
                              )}
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            ) : null}

            {activeTab === "wishlist" ? (
              <div className="afu">
                <h2 className="font-serif text-2xl  text-gray-900 mb-5">
                  My Wishlist ({wishlist.length})
                </h2>
                {wishlist.length === 0 ? (
                  <div className="text-center py-16 bg-white rounded-2xl border border-gray-100">
                    <div className="text-5xl mb-3">♡</div>
                    <p className="font-bold text-gray-700">Nothing saved yet</p>
                    <p className="text-sm text-gray-400 mt-1 mb-5">
                      Tap the heart on any dish to save it here
                    </p>
                    <button
                      onClick={() => navigate("/menu")}
                      className="bg-[#f97316] text-white font-bold px-6 py-3 rounded-xl btn-press"
                      type="button"
                    >
                      Browse Menu
                    </button>
                  </div>
                ) : (
                  <div className="grid gap-4 md:grid-cols-2">
                    {wishlist.map((product) => (
                      <div key={product.id} className="bg-white p-4 rounded-xl shadow-sm border">
                        <img
                          src={product.images?.image}
                          className="h-40 w-full object-contain mb-4"
                          alt={product.title || product.name || "Wishlist item"}
                        />
                        <h3 className="font-bold line-clamp-1">{product.title || product.name}</h3>
                        <div className="flex justify-between mt-4">
                          <button
                            onClick={() => toggleWishlist(product)}
                            className="text-red-500 text-sm"
                            type="button"
                          >
                            Remove
                          </button>
                          <button
                            onClick={() => addToCart(product)}
                            className="text-orange-500 font-bold text-sm"
                            type="button"
                          >
                            Add to Cart
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            ) : null}

            {activeTab === "address" ? (
              <div className="afu space-y-4">
                <div className="flex items-center justify-between mb-5">
                  <h2 className="font-serif text-2xl text-gray-900">Saved Addresses</h2>
                  <button
                    onClick={() => navigate("/account/addresses")}
                    className="flex items-center gap-2 bg-[#f97316] hover:bg-orange-600 text-white text-sm font-bold px-4 py-2 rounded-xl transition"
                    type="button"
                  >
                    + Manage Addresses
                  </button>
                </div>
                {addresses.map((address, index) => (
                  <div
                    key={address.id || `${address.type}-${index}`}
                    className={`bg-white rounded-2xl p-5 relative shadow-sm ${
                      index === 0 ? "border-2 border-[#f97316]" : "border border-gray-100"
                    }`}
                  >
                    {index === 0 ? (
                      <span className="absolute top-3 right-3 bg-orange-500 text-white text-[10px] font-bold px-2 py-0.5 rounded-full">
                        Default
                      </span>
                    ) : null}
                    <div className="font-bold text-gray-900 mb-1">
                      🏠 {address.type || "Address"}
                    </div>
                    <p className="text-sm text-gray-500">
                      {[
                        address.fullName,
                        address.line1,
                        address.line2,
                        address.city,
                        address.state,
                        address.postalCode,
                        address.country,
                      ]
                        .filter(Boolean)
                        .join(", ")}
                    </p>
                  </div>
                ))}
                {addresses.length === 0 && (
                  <div className="bg-gray-50 rounded-2xl p-8 text-center text-sm text-gray-400">
                    No saved addresses yet.
                  </div>
                )}
                <button
                  onClick={() => navigate("/account/addresses")}
                  className="w-full border-2 border-dashed border-gray-200 rounded-2xl py-8 text-gray-400 hover:border-orange-300 hover:text-orange-400 transition-colors font-semibold text-sm"
                  type="button"
                >
                  + Add New Address
                </button>
              </div>
            ) : null}

            {activeTab === "support" ? (
              <div className="afu">
                <div className="flex items-center justify-between mb-5">
                  <h2 className="font-serif text-2xl text-gray-900">My Support Tickets</h2>
                  {activeTicket ? (
                    <button
                      onClick={() => { setActiveTicket(null); setNewTicketOpen(false); }}
                      className="flex items-center gap-1.5 text-sm font-semibold text-gray-500 hover:text-gray-700 transition-colors"
                      type="button"
                    >
                      <X size={15} /> Back to list
                    </button>
                  ) : !newTicketOpen ? (
                    <button
                      onClick={() => setNewTicketOpen(true)}
                      type="button"
                      className="flex items-center gap-2 px-4 py-2 bg-[#f97316] text-white text-sm font-bold rounded-xl hover:bg-orange-600 transition-colors"
                    >
                      + Raise a Ticket
                    </button>
                  ) : (
                    <button
                      onClick={() => { setNewTicketOpen(false); setNewTicketError(""); setNewTicketForm({ subject:"", category:"general", priority:"medium", description:"", order_id:"" }); }}
                      className="flex items-center gap-1.5 text-sm font-semibold text-gray-500 hover:text-gray-700 transition-colors"
                      type="button"
                    >
                      <X size={15} /> Cancel
                    </button>
                  )}
                </div>

                {/* ── New ticket form ── */}
                {newTicketOpen && !activeTicket && (
                  <div className="bg-white rounded-3xl border border-gray-100 shadow-sm p-6 mb-5 space-y-4">
                    <h3 className="font-bold text-gray-800">New Support Request</h3>

                    <div>
                      <label className="block text-xs font-bold text-gray-500 mb-1.5">Subject *</label>
                      <input
                        value={newTicketForm.subject}
                        onChange={e => setNewTicketForm(p => ({ ...p, subject: e.target.value }))}
                        placeholder="Brief description of your issue"
                        className="w-full px-4 py-3 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-orange-300"
                      />
                    </div>

                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        <label className="block text-xs font-bold text-gray-500 mb-1.5">Category</label>
                        <select
                          value={newTicketForm.category}
                          onChange={e => setNewTicketForm(p => ({ ...p, category: e.target.value, order_id: e.target.value !== "order" ? "" : p.order_id }))}
                          className="w-full px-4 py-3 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-orange-300 bg-white"
                        >
                          <option value="general">General</option>
                          <option value="order">Order Issue</option>
                          <option value="billing">Billing</option>
                          <option value="technical">Technical</option>
                          <option value="other">Other</option>
                        </select>
                      </div>
                      <div>
                        <label className="block text-xs font-bold text-gray-500 mb-1.5">Priority</label>
                        <select
                          value={newTicketForm.priority}
                          onChange={e => setNewTicketForm(p => ({ ...p, priority: e.target.value }))}
                          className="w-full px-4 py-3 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-orange-300 bg-white"
                        >
                          <option value="low">Low</option>
                          <option value="medium">Medium</option>
                          <option value="high">High</option>
                          <option value="urgent">Urgent</option>
                        </select>
                      </div>
                    </div>

                    {newTicketForm.category === "order" && (
                      <div>
                        <label className="block text-xs font-bold text-gray-500 mb-1.5">Related Order *</label>
                        {orders.length === 0 ? (
                          <p className="text-sm text-gray-400 px-4 py-3 border border-gray-200 rounded-xl bg-gray-50">No orders found on your account.</p>
                        ) : (
                          <select
                            value={newTicketForm.order_id}
                            onChange={e => setNewTicketForm(p => ({ ...p, order_id: e.target.value }))}
                            className="w-full px-4 py-3 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-orange-300 bg-white"
                          >
                            <option value="">— Select an order —</option>
                            {orders.map(o => (
                              <option key={o.id} value={o.id}>
                                #{o.id} · {o.date} · {o.items?.length || 0} item{o.items?.length !== 1 ? "s" : ""} · {formatCurrency(o.total)} · {o.status}
                              </option>
                            ))}
                          </select>
                        )}
                      </div>
                    )}

                    <div>
                      <label className="block text-xs font-bold text-gray-500 mb-1.5">Description *</label>
                      <textarea
                        value={newTicketForm.description}
                        onChange={e => setNewTicketForm(p => ({ ...p, description: e.target.value }))}
                        placeholder="Describe your issue in detail…"
                        rows={4}
                        className="w-full px-4 py-3 border border-gray-200 rounded-xl text-sm resize-none focus:outline-none focus:ring-2 focus:ring-orange-300"
                      />
                    </div>

                    {newTicketError && <p className="text-xs text-red-500">{newTicketError}</p>}

                    <button
                      type="button"
                      disabled={
                        submittingNew ||
                        !newTicketForm.subject ||
                        !newTicketForm.description ||
                        (newTicketForm.category === "order" && !newTicketForm.order_id)
                      }
                      onClick={async () => {
                        setSubmittingNew(true);
                        setNewTicketError("");
                        try {
                          const descWithOrder = newTicketForm.order_id
                            ? `Order #${newTicketForm.order_id}\n\n${newTicketForm.description}`
                            : newTicketForm.description;
                          const res = await axios.post(`${API_BASE}/tickets`, {
                            customer_id:    customerUser.id,
                            customer_name:  userName,
                            customer_email: customerUser.email,
                            ...newTicketForm,
                            description: descWithOrder,
                          });
                          if (res.data.status) {
                            setTickets(prev => [res.data.ticket, ...prev]);
                            setNewTicketOpen(false);
                            setNewTicketForm({ subject:"", category:"general", priority:"medium", description:"", order_id:"" });
                          } else {
                            setNewTicketError(res.data.error || "Failed to submit.");
                          }
                        } catch (_) {
                          setNewTicketError("Something went wrong. Please try again.");
                        } finally {
                          setSubmittingNew(false);
                        }
                      }}
                      className="w-full py-3 bg-[#f97316] text-white font-extrabold rounded-xl hover:bg-orange-600 disabled:opacity-60 disabled:cursor-not-allowed transition-colors"
                    >
                      {submittingNew ? "Submitting…" : "Submit Ticket"}
                    </button>
                  </div>
                )}

                {/* ── Thread view ── */}
                {(activeTicket || loadingTicket) ? (
                  <div className="bg-white rounded-3xl border border-gray-100 shadow-sm overflow-hidden">
                    {loadingTicket ? (
                      <div className="py-16 text-center text-gray-400 text-sm">Loading…</div>
                    ) : (
                      <>
                        {/* Thread header */}
                        <div className="p-5 border-b border-gray-100">
                          <div className="flex flex-wrap items-center gap-2 mb-1">
                            <span className="text-xs font-mono text-gray-400">{activeTicket.ticket_number}</span>
                            <span className={`inline-block px-2 py-0.5 rounded-full text-xs font-semibold capitalize ${
                              { open:"bg-blue-100 text-blue-700", in_progress:"bg-yellow-100 text-yellow-700", resolved:"bg-green-100 text-green-700", closed:"bg-gray-100 text-gray-500" }[activeTicket.status] || "bg-gray-100 text-gray-500"
                            }`}>{activeTicket.status?.replace("_"," ")}</span>
                            <span className={`inline-block px-2 py-0.5 rounded-full text-xs font-semibold capitalize ${
                              { low:"bg-slate-100 text-slate-600", medium:"bg-blue-100 text-blue-600", high:"bg-orange-100 text-orange-600", urgent:"bg-red-100 text-red-700" }[activeTicket.priority] || "bg-gray-100 text-gray-500"
                            }`}>{activeTicket.priority}</span>
                          </div>
                          <h3 className="font-bold text-gray-900">{activeTicket.subject}</h3>
                          <p className="text-xs text-gray-400 mt-0.5">
                            Category: <span className="capitalize font-medium text-gray-600">{activeTicket.category}</span>
                          </p>
                        </div>

                        {/* Messages */}
                        <div className="p-5 space-y-4 max-h-96 overflow-y-auto bg-gray-50">
                          {(activeTicket.messages || []).map((msg, i) => {
                            const isAdmin = msg.sender_type === "admin";
                            return (
                              <div key={msg.id || i} className={`flex ${isAdmin ? "justify-start" : "justify-end"}`}>
                                <div className={`max-w-[80%] rounded-2xl px-4 py-3 text-sm ${
                                  isAdmin
                                    ? "bg-white border border-gray-200 text-gray-800 rounded-tl-sm shadow-sm"
                                    : "bg-[#f97316] text-white rounded-tr-sm"
                                }`}>
                                  <p className={`text-[11px] font-semibold mb-1 ${isAdmin ? "text-gray-400" : "text-orange-100"}`}>
                                    {isAdmin ? "Support Team" : "You"}
                                  </p>
                                  <p className="leading-relaxed whitespace-pre-wrap">{msg.message}</p>
                                  <p className={`text-[10px] mt-1.5 ${isAdmin ? "text-gray-400" : "text-orange-200"}`}>
                                    {msg.created_at ? new Date(msg.created_at).toLocaleDateString("en-IN", { day:"2-digit", month:"short", year:"numeric" }) : ""}
                                  </p>
                                </div>
                              </div>
                            );
                          })}
                        </div>

                        {/* Reply form */}
                        {activeTicket.status !== "closed" ? (
                          <div className="p-4 border-t border-gray-100 bg-white">
                            <p className="text-xs font-bold text-gray-500 mb-2">Add a Reply</p>
                            <div className="flex gap-2 items-end">
                              <textarea
                                value={replyText}
                                onChange={e => setReplyText(e.target.value)}
                                onKeyDown={e => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); sendTicketReply(); } }}
                                placeholder="Type your message… (Enter to send)"
                                rows={3}
                                className="flex-1 px-4 py-3 border border-gray-200 rounded-xl text-sm resize-none focus:outline-none focus:ring-2 focus:ring-orange-300"
                              />
                              <button
                                onClick={sendTicketReply}
                                disabled={sendingReply || !replyText.trim()}
                                type="button"
                                className="bg-[#f97316] text-white p-3 rounded-xl hover:bg-orange-600 disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex-shrink-0"
                              >
                                <Send size={16} />
                              </button>
                            </div>
                          </div>
                        ) : (
                          <div className="px-5 py-3 border-t border-gray-100 text-center text-xs text-gray-400 bg-white">
                            This ticket is closed.
                          </div>
                        )}
                      </>
                    )}
                  </div>
                ) : (
                  /* ── Ticket list ── */
                  <div className="space-y-3">
                    {!ticketsLoaded ? (
                      <div className="py-12 text-center text-gray-400 text-sm bg-white rounded-3xl border border-gray-100">Loading…</div>
                    ) : tickets.length === 0 ? (
                      <div className="py-12 text-center bg-white rounded-3xl border border-gray-100 shadow-sm">
                        <div className="text-4xl mb-3">🎫</div>
                        <p className="font-bold text-gray-700">No tickets yet</p>
                        <p className="text-sm text-gray-400 mt-1">Need help? Contact our support team.</p>
                      </div>
                    ) : tickets.map(t => (
                      <button
                        key={t.id}
                        onClick={() => openTicketThread(t)}
                        type="button"
                        className="w-full text-left bg-white rounded-2xl border border-gray-100 shadow-sm p-4 hover:border-orange-200 hover:shadow-md transition-all"
                      >
                        <div className="flex items-start justify-between gap-3">
                          <div className="flex-1 min-w-0">
                            <div className="flex flex-wrap items-center gap-2 mb-1">
                              <span className="text-xs font-mono text-gray-400">{t.ticket_number}</span>
                              <span className={`inline-block px-2 py-0.5 rounded-full text-xs font-semibold capitalize ${
                                { open:"bg-blue-100 text-blue-700", in_progress:"bg-yellow-100 text-yellow-700", resolved:"bg-green-100 text-green-700", closed:"bg-gray-100 text-gray-500" }[t.status] || "bg-gray-100 text-gray-500"
                              }`}>{t.status?.replace("_"," ")}</span>
                              <span className={`inline-block px-2 py-0.5 rounded-full text-xs font-semibold capitalize ${
                                { low:"bg-slate-100 text-slate-600", medium:"bg-blue-100 text-blue-600", high:"bg-orange-100 text-orange-600", urgent:"bg-red-100 text-red-700" }[t.priority] || "bg-gray-100 text-gray-500"
                              }`}>{t.priority}</span>
                            </div>
                            <p className="font-semibold text-gray-900 text-sm">{t.subject}</p>
                            <p className="text-xs text-gray-400 mt-0.5 capitalize">{t.category}</p>
                          </div>
                          <span className="text-gray-300 text-lg flex-shrink-0">›</span>
                        </div>
                      </button>
                    ))}
                  </div>
                )}
              </div>
            ) : null}

            {activeTab === "notifications" ? (
              <div className="afu space-y-4">
                <div className="flex items-center justify-between">
                  <h2 className="font-serif text-2xl text-gray-900">Notifications</h2>
                  {unreadNotifs > 0 && (
                    <button
                      type="button"
                      onClick={async () => {
                        await axios.put(`${API_BASE}/notifications/read-all`, { recipient_type: "customer", recipient_id: customerUser.id }).catch(() => {});
                        setNotifs(prev => prev.map(n => ({ ...n, is_read: true })));
                        setUnreadNotifs(0);
                      }}
                      className="flex items-center gap-1.5 text-xs font-bold text-orange-500 hover:text-orange-600 transition-colors"
                    >
                      <CheckCheck size={14} /> Mark all read
                    </button>
                  )}
                </div>

                {!notifsLoaded ? (
                  <div className="py-12 text-center text-gray-400 text-sm bg-white rounded-3xl border border-gray-100">Loading…</div>
                ) : notifs.length === 0 ? (
                  <div className="py-12 text-center bg-white rounded-3xl border border-gray-100 shadow-sm">
                    <Bell size={36} className="mx-auto mb-3 text-gray-300" />
                    <p className="font-bold text-gray-700">No notifications yet</p>
                    <p className="text-sm text-gray-400 mt-1">We'll notify you when there's an update on your tickets.</p>
                  </div>
                ) : (
                  <div className="bg-white rounded-3xl border border-gray-100 shadow-sm overflow-hidden divide-y divide-gray-50">
                    {notifs.map(n => (
                      <div key={n.id} className={`flex items-start gap-3 px-5 py-4 ${!n.is_read ? "bg-orange-50/50" : ""}`}>
                        <div className="mt-0.5 flex-shrink-0">
                          {n.entity_type === "ticket" ? (
                            <Ticket size={16} className={n.type === "ticket_reply" ? "text-orange-500" : "text-green-500"} />
                          ) : (
                            <Bell size={16} className="text-gray-400" />
                          )}
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className={`text-sm leading-snug ${!n.is_read ? "font-semibold text-gray-900" : "text-gray-700"}`}>{n.title}</p>
                          {n.body && <p className="text-xs text-gray-400 mt-0.5">{n.body}</p>}
                          <p className="text-[10px] text-gray-400 mt-1">
                            {n.created_at ? new Date(n.created_at).toLocaleDateString("en-IN", { day:"2-digit", month:"short", year:"numeric" }) : ""}
                          </p>
                        </div>
                        <div className="flex items-center gap-2 flex-shrink-0">
                          {!n.is_read && <span className="w-2 h-2 bg-orange-500 rounded-full" />}
                          <button
                            type="button"
                            onClick={async () => {
                              if (!n.is_read) {
                                await axios.put(`${API_BASE}/notifications/${n.id}/read`).catch(() => {});
                                setNotifs(prev => prev.map(x => x.id === n.id ? { ...x, is_read: true } : x));
                                setUnreadNotifs(prev => Math.max(0, prev - 1));
                              }
                              if (n.entity_type === "ticket") setActiveTab("support");
                            }}
                            className="text-xs font-semibold text-orange-500 hover:text-orange-600 transition-colors"
                          >
                            View
                          </button>
                          <button
                            type="button"
                            onClick={async () => {
                              await axios.delete(`${API_BASE}/notifications/${n.id}`).catch(() => {});
                              const wasUnread = !n.is_read;
                              setNotifs(prev => prev.filter(x => x.id !== n.id));
                              if (wasUnread) setUnreadNotifs(prev => Math.max(0, prev - 1));
                            }}
                            className="text-gray-300 hover:text-red-400 transition-colors"
                          >
                            <X size={14} />
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            ) : null}

            {activeTab === "settings" ? (
              <div className="afu space-y-4">
                <h2 className="font-serif text-2xl text-gray-900 mb-5">Account Settings</h2>

                {/* Push Notifications — real toggle */}
                <div className="bg-white rounded-2xl p-4 border border-gray-100 shadow-sm flex items-center gap-4">
                  <span className="text-2xl">{pushSubscribed ? "🔔" : "🔕"}</span>
                  <div className="flex-1">
                    <p className="font-bold text-gray-900 text-sm">Push Notifications</p>
                    <p className="text-xs text-gray-400">
                      {!pushSupported
                        ? "Not supported in this browser"
                        : pushPermission === "denied"
                        ? "Blocked — enable in browser settings"
                        : pushSubscribed
                        ? "You'll receive order updates and offers"
                        : "Order updates, offers and reminders"}
                    </p>
                  </div>
                  {pushSupported && pushPermission !== "denied" && (
                    <button
                      onClick={pushSubscribed ? pushUnsubscribe : pushSubscribe}
                      disabled={pushLoading}
                      className={`relative w-12 h-6 rounded-full transition-colors ${
                        pushSubscribed ? "bg-[#f97316]" : "bg-gray-200"
                      } disabled:opacity-50`}
                    >
                      <div className={`absolute top-1 w-4 h-4 bg-white rounded-full shadow transition-all ${
                        pushSubscribed ? "right-1" : "left-1"
                      }`} />
                    </button>
                  )}
                </div>

                {[
                  {
                    icon: "📧",
                    title: "Email Newsletters",
                    desc: "Weekly menu highlights and recipes",
                    toggle: true,
                    defaultOn: true,
                  },
                  {
                    icon: "📱",
                    title: "SMS Updates",
                    desc: "Real-time order status via SMS",
                    toggle: true,
                    defaultOn: true,
                  },
                  {
                    icon: "🌙",
                    title: "Dark Mode",
                    desc: "Easy on the eyes at night",
                    toggle: true,
                    defaultOn: false,
                  },
                ].map((setting) => (
                  <SettingRow key={setting.title} {...setting} />
                ))}
                <div className="bg-white rounded-2xl p-5 border border-gray-100 space-y-3">
                  <h3 className="font-bold text-gray-900">Danger Zone</h3>
                  <button
                    onClick={handleLogout}
                    className="w-full text-left px-4 py-3 bg-red-50 text-red-500 font-semibold text-sm rounded-xl hover:bg-red-100 transition-colors border border-red-100"
                    type="button"
                  >
                    🚪 Sign Out
                  </button>
                </div>
              </div>
            ) : null}
          </div>
        </div>
      </div>
      <Footer />

      {/* Rate Order Modal */}
      {ratingOrder && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-gray-900/60 backdrop-blur-sm p-4">
          <div className="bg-white rounded-3xl shadow-2xl w-full max-w-md overflow-hidden">
            <div className="flex items-center justify-between p-5 border-b border-gray-100">
              <h2 className="text-lg font-black text-gray-900">Rate Order #{ratingOrder.id}</h2>
              <button onClick={() => setRatingOrder(null)} className="p-2 hover:bg-gray-100 rounded-full transition" type="button">
                <X className="w-5 h-5 text-gray-500" />
              </button>
            </div>

            <div className="p-5 space-y-5">
              <p className="text-sm text-gray-500">Rate each item in your order to help us improve.</p>

              {/* Items to rate */}
              <div className="space-y-3">
                {(ratingOrder.items || []).map((item, i) => {
                  const product = item.item || item;
                  return (
                    <div key={i} className="flex items-center gap-3 bg-gray-50 rounded-xl p-3">
                      {product?.images?.image && (
                        <img src={product.images.image} alt={product?.name} className="w-10 h-10 rounded-lg object-cover flex-shrink-0" />
                      )}
                      <span className="text-sm font-semibold text-gray-800 flex-1 truncate">{product?.name || "Item"}</span>
                    </div>
                  );
                })}
              </div>

              {/* Overall rating */}
              <div>
                <label className="text-xs font-bold text-gray-500 uppercase tracking-wider mb-2 block">Overall Rating</label>
                <div className="flex items-center gap-1">
                  {[1, 2, 3, 4, 5].map((s) => (
                    <button
                      key={s}
                      type="button"
                      onClick={() => setRatingForm((f) => ({ ...f, rating: s }))}
                      className="text-3xl transition-transform hover:scale-110"
                    >
                      <span className={s <= ratingForm.rating ? "text-amber-400" : "text-gray-300"}>★</span>
                    </button>
                  ))}
                  <span className="ml-2 text-sm font-bold text-gray-600">{ratingForm.rating}/5</span>
                </div>
              </div>

              {/* Comment */}
              <div>
                <label className="text-xs font-bold text-gray-500 uppercase tracking-wider mb-2 block">Your Comment</label>
                <textarea
                  rows={3}
                  value={ratingForm.comment}
                  onChange={(e) => setRatingForm((f) => ({ ...f, comment: e.target.value }))}
                  placeholder="How was your experience? Share details about the food, delivery, packaging..."
                  className="w-full border border-gray-200 rounded-xl px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-orange-200 resize-none"
                />
              </div>
            </div>

            <div className="p-5 border-t border-gray-100 flex gap-3">
              <button onClick={() => setRatingOrder(null)} className="flex-1 border border-gray-200 text-gray-600 font-bold py-2.5 rounded-xl hover:bg-gray-50 transition text-sm" type="button">
                Cancel
              </button>
              <button
                type="button"
                disabled={submittingRating}
                onClick={async () => {
                  if (!ratingForm.comment.trim()) return;
                  setSubmittingRating(true);
                  try {
                    const itemsToRate = (ratingOrder.items || []).filter(i => i.menu_item_id || i.item?.id);
                    await Promise.all(
                      itemsToRate.map((item) =>
                        axios.post(`${API_BASE}/reviews`, {
                          menu_item_id: item.menu_item_id || item.item?.id,
                          user_id: customerUser.id,
                          user_name: customerUser.name || `${customerUser.firstName || ""} ${customerUser.lastName || ""}`.trim() || "Customer",
                          rating: ratingForm.rating,
                          comment: ratingForm.comment,
                          order_id: ratingOrder.id,
                        }).catch(() => {})
                      )
                    );
                    setFeedback({ type: "success", text: "Thanks for your review! It will appear after approval." });
                  } catch {
                    setFeedback({ type: "error", text: "Could not submit review. Please try again." });
                  }
                  setSubmittingRating(false);
                  setRatingOrder(null);
                }}
                className="flex-1 bg-gradient-to-r from-orange-500 to-red-500 text-white font-bold py-2.5 rounded-xl hover:shadow-md transition text-sm disabled:opacity-60"
              >
                {submittingRating ? "Submitting..." : "Submit Review"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Order Detail Drawer */}
      {selectedOrder && (
        <div className="fixed inset-0 z-50 flex justify-end">
          {/* Backdrop */}
          <div
            className="absolute inset-0 bg-gray-900/50 backdrop-blur-sm"
            onClick={() => setSelectedOrder(null)}
          />

          {/* Panel */}
          <div className="relative w-full max-w-md bg-white h-full shadow-2xl flex flex-col overflow-hidden animate-in slide-in-from-right duration-300">
            {/* Header */}
            <div className="flex items-center justify-between p-5 border-b border-gray-100 bg-white flex-shrink-0">
              <div>
                <h2 className="text-lg font-black text-gray-900">Order #{selectedOrder.id}</h2>
                <p className="text-xs text-gray-400 mt-0.5">{selectedOrder.date}</p>
              </div>
              <div className="flex items-center gap-3">
                <OrderStatusBadge status={selectedOrder.status} />
                <button
                  onClick={() => setSelectedOrder(null)}
                  className="p-2 hover:bg-gray-100 rounded-full transition"
                  type="button"
                >
                  <X className="w-5 h-5 text-gray-500" />
                </button>
              </div>
            </div>

            {/* Scrollable body */}
            <div className="flex-1 overflow-y-auto p-5 space-y-5">
              {/* Items */}
              <div>
                <p className="text-xs font-bold text-gray-400 uppercase tracking-wider mb-3">
                  Items ({selectedOrder.items?.length || 0})
                </p>
                <div className="space-y-3">
                  {(selectedOrder.items || []).map((item, i) => {
                    const product = item.item || item;
                    return (
                      <div key={i} className="flex items-center gap-3 bg-gray-50 rounded-2xl p-3">
                        {product?.images?.image ? (
                          <img
                            src={product.images.image}
                            alt={product?.name}
                            className="w-14 h-14 rounded-xl object-cover flex-shrink-0 shadow-sm"
                          />
                        ) : (
                          <div className="w-14 h-14 rounded-xl bg-gray-200 flex items-center justify-center text-2xl flex-shrink-0">
                            🍽
                          </div>
                        )}
                        <div className="flex-1 min-w-0">
                          <p className="font-semibold text-sm text-gray-800 truncate">
                            {product?.name || item?.name || "Item"}
                          </p>
                          {product?.category?.name && (
                            <p className="text-[10px] text-orange-500 font-semibold mt-0.5">
                              {product.category.name}
                            </p>
                          )}
                          <p className="text-xs text-gray-400 mt-0.5">
                            {formatCurrency(item.price)} × {item.quantity}
                          </p>
                        </div>
                        <span className="font-black text-gray-800 text-sm flex-shrink-0">
                          {formatCurrency(Number(item.price) * Number(item.quantity))}
                        </span>
                      </div>
                    );
                  })}
                </div>
              </div>

              {/* Price breakdown */}
              <div className="bg-gray-50 rounded-2xl p-4 space-y-2 text-sm">
                <p className="text-xs font-bold text-gray-400 uppercase tracking-wider mb-3">
                  Price Summary
                </p>
                {(() => {
                  const subtotal = selectedOrder.total || 0;
                  const tax = subtotal * 0.1;
                  const delivery = subtotal >= 30 ? 0 : 3.99;
                  const grand = subtotal + tax + delivery;
                  return (
                    <>
                      <div className="flex justify-between text-gray-500">
                        <span>Subtotal</span>
                        <span>{formatCurrency(subtotal)}</span>
                      </div>
                      <div className="flex justify-between text-gray-500">
                        <span>Tax (10%)</span>
                        <span>{formatCurrency(tax)}</span>
                      </div>
                      <div className="flex justify-between text-gray-500">
                        <span>Delivery</span>
                        <span>
                          {delivery === 0 ? (
                            <span className="text-green-600 font-semibold">Free</span>
                          ) : (
                            formatCurrency(delivery)
                          )}
                        </span>
                      </div>
                      <div className="flex justify-between font-black text-gray-900 text-base pt-2 border-t border-gray-200">
                        <span>Total</span>
                        <span className="text-[#f97316]">{formatCurrency(grand)}</span>
                      </div>
                    </>
                  );
                })()}
              </div>
            </div>

            {/* Footer actions */}
            <div className="flex-shrink-0 p-5 border-t border-gray-100 bg-white space-y-2">
              <button
                onClick={() => { handleReorder(selectedOrder); setSelectedOrder(null); }}
                className="w-full bg-gradient-to-r from-orange-500 to-red-500 text-white py-3 rounded-2xl font-bold hover:shadow-md transition"
                type="button"
              >
                🔄 Reorder
              </button>
              <div className="flex gap-2">
                {selectedOrder.status !== "delivered" &&
                  selectedOrder.status !== "cancelled" &&
                  selectedOrder.status !== "Delivered" && (
                    <button
                      onClick={() => { navigate(`/track/${selectedOrder.id}`); setSelectedOrder(null); }}
                      className="flex-1 bg-orange-50 text-orange-600 py-2.5 rounded-2xl font-bold text-sm hover:bg-orange-100 transition"
                      type="button"
                    >
                      📦 Track
                    </button>
                  )}
                <button
                  onClick={() => setFeedback({ type: "success", text: "Invoice download will be available soon." })}
                  className="flex-1 bg-gray-100 text-gray-600 py-2.5 rounded-2xl font-bold text-sm hover:bg-gray-200 transition"
                  type="button"
                >
                  📄 Invoice
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
};
